// SEQ-169 hire comments: the audit-notes indicator that sits beside a row.
//
// Two independent signals, deliberately kept apart:
//   the coloured dot is the shared needs-review flag, the same value for every
//   member of the organization, read from the entity's audit_flag column;
//   the red bubble is the current viewer's unread count and is personal.
//
// Self-contained so it can be dropped beside a variation table row or a hire
// register card without either owning the other's layout. Every click is
// stopped here, so mounting it inside a clickable row does not trigger that
// row's own handler.

import type { CSSProperties } from "react";
import { MessageSquare } from "lucide-react";
import { flagAppearance } from "../lib/entityComments";

interface Props {
  /** Denormalised needs-review flag from the entity's audit_flag column. */
  flag: string | null | undefined;
  /** Unread comments for the current viewer. Zero hides the bubble. */
  unreadCount: number;
  /** Opens the audit notes for this entity. */
  onOpen: () => void;
  /** Tooltip on the notes button. */
  label?: string;
  /** Layout hook for the host row. */
  style?: CSSProperties;
}

export function EntityCommentsBadge({
  flag,
  unreadCount,
  onOpen,
  label = "Audit Notes",
  style,
}: Props) {
  const appearance = flagAppearance(flag);

  // "0 unread" on every clean row is noise: a screen reader user tabbing a
  // thirty-row register would hear it thirty times. The flag state stays,
  // because "All Good" is real information about that row and a zero count is
  // not.
  const accessibleName = unreadCount > 0
    ? `${label}, ${appearance.label}, ${unreadCount} unread`
    : `${label}, ${appearance.label}`;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ...style,
      }}
    >
      <span
        // Decorative: the flag state is in the button's accessible name above.
        // The title stays for the sighted mouse user.
        aria-hidden="true"
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: appearance.dot,
          display: "inline-block",
          flexShrink: 0,
          boxShadow: appearance.glow,
        }}
        title={appearance.label}
      />
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          title={label}
          // The dot's colour and the bubble's number are both visual only. This
          // carries the flag state and the unread count into the accessible
          // name, so a screen reader announces what a sighted user can see.
          aria-label={accessibleName}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            borderRadius: "4px",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--primary-color)";
            e.currentTarget.style.background = "var(--bg-tertiary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--text-muted)";
            e.currentTarget.style.background = "none";
          }}
        >
          <MessageSquare size={15} />
        </button>
        {unreadCount > 0 && (
          <div
            // The pulse lives in CSS, not inline, so the reduced-motion block in
            // index.css has a selector to switch it off. Inline animation cannot
            // be disabled by any media query.
            className="audit-note-unread-pulse"
            // Decorative: without this the bubble is announced as a bare "3" on
            // top of the button's "3 unread".
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              backgroundColor: "var(--color-error)",
              color: "white",
              fontSize: "0.6rem",
              fontWeight: "bold",
              borderRadius: "10px",
              padding: "2px 5px",
              minWidth: "16px",
              textAlign: "center",
              lineHeight: 1,
              pointerEvents: "none",
            }}
            title={`${unreadCount} unread comment${unreadCount > 1 ? "s" : ""}`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </div>
        )}
      </div>
    </div>
  );
}
